import React, { useRef, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import './AutoLogoutModal.css';
import { DEFAULT_AUTO_LOGOUT_SETTING, LAST_ACTIVITY } from '../../global/constants';
import { logout } from '../../services/security-services/SecurityActions';

// Shared across tabs so one tab's activity keeps the others alive. Written at
// most once every ACTIVITY_WRITE_INTERVAL to keep a busy Gantt from hammering
// localStorage on every mouse move.
const ACTIVITY_WRITE_INTERVAL = 5000;
// The shared timestamp only means "another tab" when it is meaningfully newer
// than this tab's own last activity — the two are otherwise written from the
// same events and tie at the moment the timer fires.
const OTHER_TAB_MARGIN = 1000;
let lastActivityWrite = 0;

const publishActivity = (now) => {
    if (now - lastActivityWrite < ACTIVITY_WRITE_INTERVAL) return;
    lastActivityWrite = now;
    try {
        localStorage.setItem(LAST_ACTIVITY, String(now));
    } catch {
        // localStorage unavailable — fall back to this tab's own timer
    }
};

const lastActivityAcrossTabs = () => Number(localStorage.getItem(LAST_ACTIVITY)) || 0;

function AutoLogoutWrapper({ children }) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [showModal, setShowModal] = useState(false);
    
    // Auto-logout timeout from portfolio settings. Three cases, and the old
    // `autoLogoutTimeout || DEFAULT` got the first one wrong:
    //   - configured "None" (stored as 0)  -> DISABLED, not the 30-min default;
    //   - settings not fetched yet ({})    -> off, so a timer the user may have
    //     configured away can never fire in the gap before they load;
    //   - settings loaded without the field -> the default still applies. An
    //     absent field must not leave a security control silently off forever,
    //     and the slice starts as {} so "empty" is the only "not loaded" signal.
    const portfolioDetails = useSelector(state => state.portfolio.details);
    const detailsLoaded = !!portfolioDetails && Object.keys(portfolioDetails).length > 0;
    const configuredTimeout = portfolioDetails?.autoLogoutTimeout;
    const autoLogoutTimeout = configuredTimeout ?? DEFAULT_AUTO_LOGOUT_SETTING;
    const isAutoLogoutEnabled =
        configuredTimeout != null ? configuredTimeout > 0 : detailsLoaded;

    // Seeded at mount: a tab the user never touches has been "active" since it
    // was opened, so an all-idle session still times out here.
    const ownLastActivity = useRef(Date.now());

    const recordActivity = () => {
        const now = Date.now();
        ownLastActivity.current = now;
        publishActivity(now);
    };

    // logout() clears the JWT and every session-bound key, drops the cookies and
    // sessionStorage, and resets the store.
    const endSession = () => dispatch(logout());

    const onIdle = () => {
        if (!isAutoLogoutEnabled) return;
        // Idle is a property of the user, not of a tab: react-idle-timer only
        // sees events in its own tab, so a backgrounded tab reaches its timeout
        // while the user is working in another one. Since going idle now ends
        // the session, acting on that alone would log people out mid-edit.
        // (The library's own crossTab needs BroadcastChannel/MessageChannel,
        // which the jsdom test environment does not provide.)
        if (lastActivityAcrossTabs() > ownLastActivity.current + OTHER_TAB_MARGIN) {
            reset();
            return;
        }
        // Tear the session down the moment the timer fires. This used to live in
        // the OK handler, so a user who walked away kept a fully valid token —
        // the modal read "you have been logged out" while the API still answered
        // 200 to that token, which is the opposite of an inactivity logout.
        endSession();
        setShowModal(true);
    };

    const handleClose = () => {
        setShowModal(false);
        navigate('/login');
    };

    const { reset } = useIdleTimer({
        // react-idle-timer rejects timeout 0 — feed the default; `disabled`
        // is what actually keeps the "None" configuration inert.
        timeout: autoLogoutTimeout > 0 ? autoLogoutTimeout : DEFAULT_AUTO_LOGOUT_SETTING,
        onIdle,
        onAction: recordActivity,
        debounce: 500,
        disabled: !isAutoLogoutEnabled,
    });

    return (
        <>
            {children}

            {showModal && (
                <div className="auto-logout-modal-overlay">
                    <div className="auto-logout-modal">
                        <h5 className="modal-title">{t('sessionExpired.title')}</h5>
                        <p className="modal-text">{t('sessionExpired.message')}</p>
                        <div className="modal-actions">
                            <button className="modal-btn" onClick={handleClose}>
                                {t('sessionExpired.okButton')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default AutoLogoutWrapper;
